import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import { supabase } from '../api/supabase';
import mammoth from 'mammoth';
import html2pdf from 'html2pdf.js';

/**
 * Convertit un blob DOCX en blob PDF en utilisant une API de conversion en ligne
 * Cette méthode préserve parfaitement la mise en page, les images et les en-têtes
 *
 * @param {Blob} docxBlob - Le fichier DOCX à convertir
 * @param {string} fileName - Nom du fichier pour la conversion
 * @returns {Promise<Blob>} - Le fichier PDF généré
 */
const convertDocxToPdf = async (docxBlob, fileName = 'document.docx') => {
  try {
    console.log('🔄 Début conversion DOCX → PDF via API');

    // Utiliser l'API CloudConvert (gratuit jusqu'à 25 conversions/jour)
    // Alternative: ConvertAPI, iLovePDF API, Cloudmersive
    const CONVERSION_API_URL = process.env.REACT_APP_LIBREOFFICE_API_URL;

    if (!CONVERSION_API_URL) {
      console.warn('⚠️  Aucune API de conversion configurée. Tentative de conversion locale...');
      // Fallback: conversion basique avec mammoth + html2pdf (moins précise)
      return await convertDocxToPdfLocal(docxBlob);
    }

    // Créer un FormData pour l'upload
    const formData = new FormData();
    formData.append('file', docxBlob, fileName);

    // Envoyer la requête de conversion
    const response = await fetch(CONVERSION_API_URL, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Erreur API de conversion: ${response.statusText}`);
    }

    const pdfBlob = await response.blob();
    console.log('✅ Conversion PDF terminée via API, taille:', pdfBlob.size);
    return pdfBlob;

  } catch (error) {
    console.error('❌ Erreur conversion DOCX → PDF:', error);
    console.log('🔄 Tentative de conversion locale en fallback...');
    // Fallback vers la conversion locale
    return await convertDocxToPdfLocal(docxBlob);
  }
};

/**
 * Conversion locale DOCX → PDF (fallback, moins précise)
 * Préserve le contenu mais peut perdre la mise en page complexe
 * VERSION AMÉLIORÉE avec meilleure gestion des images et du formatage
 */
const convertDocxToPdfLocal = async (docxBlob) => {
  try {
    console.log('🔄 Conversion locale DOCX → HTML → PDF (version améliorée)');

    // 1. Convertir DOCX en HTML avec mammoth (options optimisées)
    const arrayBuffer = await docxBlob.arrayBuffer();
    const result = await mammoth.convertToHtml({
      arrayBuffer,
      options: {
        includeDefaultStyleMap: true,
        includeEmbeddedStyleMap: true,
        // Convertir les images avec redimensionnement automatique
        convertImage: mammoth.images.imgElement(function(image) {
          return image.read("base64").then(function(imageBuffer) {
            // Créer une image avec dimensions contraintes
            return {
              src: "data:" + image.contentType + ";base64," + imageBuffer,
              // Ajouter des attributs de style directement
              style: "max-width: 120px; max-height: 60px; width: auto; height: auto; object-fit: contain;"
            };
          });
        }),
        // Préserver les tableaux et structures
        styleMap: [
          "p[style-name='Heading 1'] => h1:fresh",
          "p[style-name='Heading 2'] => h2:fresh",
          "p[style-name='Heading 3'] => h3:fresh",
          "p[style-name='Title'] => h1.title:fresh",
          "r[style-name='Strong'] => strong",
          "p[style-name='Normal'] => p:fresh"
        ]
      }
    });
    
    const html = result.value;
    console.log('✅ HTML généré, longueur:', html.length);

    // 2. Créer un conteneur temporaire avec styles CSS améliorés
    const container = document.createElement('div');
    container.innerHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            /* === PAGE SETUP === */
            @page {
              size: A4;
              margin: 15mm 15mm 15mm 15mm;
            }
            
            /* === BODY === */
            body {
              font-family: 'Calibri', 'Arial', sans-serif;
              font-size: 10pt;
              line-height: 1.3;
              color: #000000;
              margin: 0;
              padding: 0;
              background: white;
            }
            
            /* === IMAGES - CONTRÔLE STRICT === */
            img {
              /* Dimensions maximales absolues */
              max-width: 100px !important;
              max-height: 50px !important;
              width: auto !important;
              height: auto !important;
              
              /* Comportement */
              object-fit: contain !important;
              display: block;
              margin: 5px 0;
              
              /* Anti-débordement */
              overflow: hidden;
              page-break-inside: avoid;
            }
            
            /* Logo en haut du document (premier paragraphe) */
            body > p:first-child img,
            body > p:first-of-type img,
            div:first-child img {
              max-width: 90px !important;
              max-height: 45px !important;
              margin-bottom: 8px;
            }
            
            /* Images dans les tableaux */
            table img,
            td img,
            th img {
              max-width: 80px !important;
              max-height: 40px !important;
            }
            
            /* === TITRES === */
            h1, h2, h3 {
              margin: 10px 0 6px 0;
              page-break-after: avoid;
              font-weight: bold;
              color: #000000;
            }
            
            h1 {
              font-size: 14pt;
              text-align: center;
              margin-top: 5px;
            }
            
            h1.title {
              font-size: 16pt;
              font-weight: bold;
              text-align: center;
              margin: 8px 0 12px 0;
            }
            
            h2 {
              font-size: 12pt;
              margin-top: 12px;
            }
            
            h3 {
              font-size: 11pt;
              margin-top: 10px;
            }
            
            /* === PARAGRAPHES === */
            p {
              margin: 4px 0;
              text-align: left;
              line-height: 1.4;
            }
            
            /* === TABLEAUX === */
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 8px 0;
              page-break-inside: auto;
              font-size: 9pt;
            }
            
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            
            td, th {
              border: 0.5pt solid #000000;
              padding: 4px 6px;
              text-align: left;
              vertical-align: top;
              line-height: 1.3;
            }
            
            th {
              background-color: #f5f5f5;
              font-weight: bold;
              text-align: center;
            }
            
            /* Cellules vides */
            td:empty::after {
              content: '\\00a0';
            }
            
            /* === TEXTE === */
            strong, b {
              font-weight: bold;
            }
            
            em, i {
              font-style: italic;
            }
            
            u {
              text-decoration: underline;
            }
            
            /* === LISTES === */
            ul, ol {
              margin: 6px 0;
              padding-left: 20px;
            }
            
            li {
              margin: 3px 0;
              line-height: 1.4;
            }
            
            /* === SECTIONS SPÉCIALES === */
            .header {
              text-align: center;
              margin-bottom: 15px;
            }
            
            .footer {
              margin-top: 20px;
              font-size: 8pt;
              text-align: center;
              color: #666;
            }
            
            /* === ÉVITER LES COUPURES === */
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid;
            }
            
            table, figure, img {
              page-break-inside: avoid;
            }
            
            /* === ESPACES === */
            .spacer {
              height: 10px;
            }
            
            /* === SIGNATURE === */
            .signature-block {
              margin-top: 30px;
              page-break-inside: avoid;
            }
            
            /* === TOTAL / PRIX === */
            .total-row {
              font-weight: bold;
              background-color: #f0f0f0;
            }
            
            /* === CORRECTION SPÉCIALE POUR MASTER SKI === */
            p:contains("MASTERSKI"),
            p:contains("Masterski"),
            p:contains("Master Ski") {
              margin-bottom: 5px;
            }
          </style>
        </head>
        <body>
          ${html}
        </body>
      </html>
    `;

    // 3. Nettoyer le HTML généré (supprimer les balises vides, optimiser)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Forcer la réduction de toutes les images dans le DOM temporaire
    const images = tempDiv.getElementsByTagName('img');
    for (let img of images) {
      img.style.maxWidth = '100px';
      img.style.maxHeight = '50px';
      img.style.width = 'auto';
      img.style.height = 'auto';
      img.style.objectFit = 'contain';
    }

    // 4. Options optimisées pour html2pdf
    const opt = {
      margin: [12, 12, 12, 12], // top, right, bottom, left (en mm)
      filename: 'document.pdf',
      image: { 
        type: 'jpeg', 
        quality: 0.95 
      },
      html2canvas: {
        scale: 2, // Qualité de rendu (2 = haute qualité)
        useCORS: true,
        logging: false,
        letterRendering: true,
        allowTaint: true,
        scrollY: 0,
        scrollX: 0,
        // CRITIQUE: Redimensionner les images AVANT le rendu
        onclone: function(clonedDoc) {
          console.log('🔧 Redimensionnement des images dans le document cloné...');
          
          // Appliquer les styles directement sur le body
          const body = clonedDoc.body;
          if (body) {
            body.style.fontFamily = 'Calibri, Arial, sans-serif';
            body.style.fontSize = '10pt';
            body.style.lineHeight = '1.3';
          }
          
          // Forcer TOUTES les images à être petites
          const imgs = clonedDoc.getElementsByTagName('img');
          console.log(`📸 Nombre d'images trouvées: ${imgs.length}`);
          
          for (let i = 0; i < imgs.length; i++) {
            const img = imgs[i];
            
            // Définir les dimensions MAX absolues
            img.style.maxWidth = '100px';
            img.style.maxHeight = '50px';
            img.style.width = 'auto';
            img.style.height = 'auto';
            img.style.objectFit = 'contain';
            img.style.display = 'block';
            img.style.margin = '5px 0';
            
            // Cas spécial: logo en haut (premier élément)
            if (i === 0) {
              img.style.maxWidth = '90px';
              img.style.maxHeight = '45px';
            }
            
            // Forcer l'attribut HTML aussi (backup)
            img.setAttribute('width', 'auto');
            img.setAttribute('height', 'auto');
            
            console.log(`  ✓ Image ${i + 1} redimensionnée`);
          }
          
          // Optimiser les tableaux
          const tables = clonedDoc.getElementsByTagName('table');
          for (let table of tables) {
            table.style.width = '100%';
            table.style.borderCollapse = 'collapse';
            table.style.fontSize = '9pt';
          }
          
          console.log('✅ Redimensionnement terminé');
        }
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compress: true,
        precision: 2
      },
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: ['img', 'table', 'tr']
      }
    };

    // 5. Générer le PDF avec html2pdf
    console.log('🎨 Début de la génération PDF avec html2pdf.js...');
    
    const pdfBlob = await html2pdf()
      .set(opt)
      .from(container)
      .output('blob');

    console.log('✅ Conversion PDF locale terminée avec succès');
    console.log('📄 Taille du PDF:', (pdfBlob.size / 1024).toFixed(2), 'KB');
    
    return pdfBlob;

  } catch (error) {
    console.error('❌ Erreur conversion locale DOCX → PDF:', error);
    console.error('Stack trace:', error.stack);
    throw new Error('Erreur lors de la conversion en PDF: ' + error.message);
  }
};

/**
 * Génère un document Word à partir d'un template
 *
 * GUIDE DE DÉPANNAGE:
 *
 * Erreur "Multi error" ou "Template error":
 * - Vérifiez que toutes les variables utilisées dans le template existent dans `data`
 * - Les variables dans le template doivent être au format {{nom_variable}}
 * - Assurez-vous qu'il n'y a pas d'espaces dans les noms de variables: {{client_name}} ✓, {{client name}} ✗
 *
 * Erreur "The file you are trying to download does not exist":
 * - Le template n'existe pas dans Supabase Storage bucket 'templates'
 * - Vérifiez le nom exact du fichier: Contrat.docx, Confirmation.docx, etc.
 *
 * Erreur upload Supabase:
 * - Vérifiez que le bucket 'documents' existe
 * - Vérifiez les permissions du bucket
 *
 * Erreur sauvegarde DB:
 * - Vérifiez que la table 'documents' existe
 * - Vérifiez les permissions RLS (Row Level Security)
 * 
 * Problèmes de formatage PDF:
 * - Le logo/images sont trop grandes: optimisez les images dans le template DOCX (max 200px)
 * - Le texte est décalé: vérifiez les marges et l'espacement dans le template
 * - Les tableaux sont cassés: utilisez des tableaux simples sans fusion complexe
 */
export const generateDocument = async (type, data, charterId = null, contractType = 'bateaux') => {
  console.log('=== DÉBUT GÉNÉRATION DOCUMENT ===');
  console.log('Type de document:', type);
  console.log('Charter ID:', charterId);
  console.log('Type de contrat:', contractType);
  console.log('Données:', JSON.stringify(data, null, 2));

  try {
    // 1. Télécharger le template depuis Supabase Storage
    const cacheBuster = Date.now();
    let templateExtension = '.docx';
    let signedUrlData, signedUrlError;
    let templateFileName = type;

    // Construire le nom du fichier template selon le contractType
    if (contractType === 'bateaux') {
      const yachtTemplates = {
        'Contrat': 'contrat-yacht',
        'Confirmation': 'confirmation-yacht',
        'Facturation': 'facturation-yacht'
      };
      templateFileName = yachtTemplates[type] || `${type}-yacht`;
    } else if (contractType === 'seabob') {
      const seabobTemplates = {
        'Contrat': 'contrat-seabob',
        'Confirmation': 'confirmation-seabob',
        'Facturation': 'facturation-seabob'
      };
      templateFileName = seabobTemplates[type] || `${type}-seabob`;
    } else if (contractType === 'jetski') {
      const jetskiTemplates = {
        'Contrat': 'contrat-jetski',
        'Confirmation': 'confirmation-jetski',
        'Facturation': 'facturation-jetski'
      };
      templateFileName = jetskiTemplates[type] || `${type}-jetski`;
    } else if (contractType === 'lunch') {
      const lunchTemplates = {
        'Contrat': 'contrat-lunch',
        'Confirmation': 'confirmation-lunch',
        'Facture': 'facture-lunch',
        'Facturation': 'facture-lunch'
      };
      templateFileName = lunchTemplates[type] || `${type.toLowerCase()}-lunch`;
    } else if (contractType) {
      templateFileName = `${type.toLowerCase()}-${contractType.toLowerCase()}`;
    }

    console.log('📥 Tentative téléchargement template:', `${templateFileName}.docx?v=${cacheBuster}`);

    // Essayer d'abord le format DOCX avec le nom spécifique
    ({ data: signedUrlData, error: signedUrlError } = await supabase
      .storage
      .from('templates')
      .createSignedUrl(`${templateFileName}.docx`, 60));

    // Si pas trouvé avec le nom spécifique, essayer le nom simple
    if (signedUrlError && contractType !== 'bateaux') {
      console.log(`⚠️  ${templateFileName}.docx non trouvé, tentative avec ${type}.docx...`);
      templateFileName = type;

      ({ data: signedUrlData, error: signedUrlError } = await supabase
        .storage
        .from('templates')
        .createSignedUrl(`${type}.docx`, 60));
    }

    if (signedUrlError) {
      console.error('❌ Erreur création URL signée:', signedUrlError);
      throw new Error(`Template non trouvé: ${templateFileName}.docx n'existe pas dans Supabase Storage`);
    }

    console.log(`✅ Template trouvé: ${templateFileName}${templateExtension}`);

    // 2. Télécharger le template
    const response = await fetch(signedUrlData.signedUrl, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });

    if (!response.ok) {
      throw new Error(`Erreur téléchargement: ${response.statusText}`);
    }

    const templateFile = await response.blob();
    console.log(`✅ Template téléchargé (${(templateFile.size / 1024).toFixed(2)} KB)`);

    // 3. Convertir en ArrayBuffer
    const arrayBuffer = await templateFile.arrayBuffer();

    // 4. Charger dans PizZip
    const zip = new PizZip(arrayBuffer);

    // 5. Créer le document avec Docxtemplater
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters: { start: '{{', end: '}}' }
    });

    // 6. Remplacer les variables
    console.log('🔄 Rendu du document avec les données...');
    doc.render(data);
    console.log('✅ Rendu terminé');

    // 7. Générer le DOCX
    const blob = doc.getZip().generate({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    console.log('📦 Blob DOCX généré:', (blob.size / 1024).toFixed(2), 'KB');

    // 8. Créer les noms de fichiers
    const clientName = (data.client_name || 'Client')
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '');
    
    const charterDate = data.charter_date 
      ? data.charter_date.replace(/\//g, '-') 
      : '';
    
    const timestamp = Date.now();

    let docxFileName, pdfFileName;

    if (contractType === 'bateaux') {
      const vesselName = data.vessel_name
        ? data.vessel_name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '')
        : (data.brand_of_vessel && data.length
          ? `${data.brand_of_vessel}_${data.length}`.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '')
          : 'Vessel');
      docxFileName = `${type}_${clientName}_${vesselName}_${charterDate}_${timestamp}.docx`;
      pdfFileName = `${type}_${clientName}_${vesselName}_${charterDate}_${timestamp}.pdf`;
    } else if (contractType === 'seabob') {
      docxFileName = `${type}_${clientName}_Seabob_${charterDate}_${timestamp}.docx`;
      pdfFileName = `${type}_${clientName}_Seabob_${charterDate}_${timestamp}.pdf`;
    } else if (contractType === 'jetski') {
      docxFileName = `${type}_${clientName}_Jetski_${charterDate}_${timestamp}.docx`;
      pdfFileName = `${type}_${clientName}_Jetski_${charterDate}_${timestamp}.pdf`;
    } else {
      docxFileName = `${type}_${clientName}_${charterDate}_${timestamp}.docx`;
      pdfFileName = `${type}_${clientName}_${charterDate}_${timestamp}.pdf`;
    }

    console.log('📝 Noms des fichiers:', { docxFileName, pdfFileName });

    // 9. Convertir DOCX en PDF (version améliorée)
    console.log('🔄 Début conversion DOCX → PDF (version optimisée)...');
    let pdfBlob = null;
    try {
      pdfBlob = await convertDocxToPdf(blob, docxFileName);
      console.log('✅ Conversion PDF réussie:', (pdfBlob.size / 1024).toFixed(2), 'KB');
    } catch (pdfError) {
      console.error('⚠️  Erreur conversion PDF (non bloquante):', pdfError.message);
      console.log('📄 Le document DOCX sera disponible sans PDF');
      pdfBlob = null;
    }

    // 10. Upload le DOCX vers Supabase Storage
    console.log('☁️  Upload DOCX vers Supabase Storage...');
    const { error: uploadDocxError } = await supabase
      .storage
      .from('documents')
      .upload(`docx/${docxFileName}`, blob, {
        contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

    if (uploadDocxError) {
      console.error('❌ Erreur upload DOCX:', uploadDocxError);
      throw uploadDocxError;
    }
    console.log('✅ Upload DOCX réussi');

    // 11. Upload le PDF vers Supabase Storage (si disponible)
    let pdfUrlData = null;
    if (pdfBlob) {
      console.log('☁️  Upload PDF vers Supabase Storage...');
      const { error: uploadPdfError } = await supabase
        .storage
        .from('documents')
        .upload(`pdf/${pdfFileName}`, pdfBlob, {
          contentType: 'application/pdf',
        });

      if (uploadPdfError) {
        console.error('⚠️  Erreur upload PDF (non bloquante):', uploadPdfError);
        console.log('📄 Le document DOCX sera disponible sans PDF');
      } else {
        console.log('✅ Upload PDF réussi');
        pdfUrlData = supabase
          .storage
          .from('documents')
          .getPublicUrl(`pdf/${pdfFileName}`);
      }
    }

    // 12. Obtenir l'URL publique du DOCX
    const { data: docxUrlData } = supabase
      .storage
      .from('documents')
      .getPublicUrl(`docx/${docxFileName}`);

    console.log('🔗 URL DOCX:', docxUrlData.publicUrl);
    if (pdfUrlData) {
      console.log('🔗 URL PDF:', pdfUrlData.data.publicUrl);
    }

    // 13. Sauvegarder dans la base de données
    console.log('💾 Sauvegarde en base de données...');
    const { data: dbRecord, error: dbError } = await supabase
      .from('documents')
      .insert({
        type: type,
        client_name: data.client_name,
        charter_date: data.charter_date,
        charter_id: charterId,
        category: contractType,
        document_data: data,
        docx_url: docxUrlData.publicUrl,
        pdf_url: pdfUrlData ? pdfUrlData.data.publicUrl : null,
      })
      .select()
      .single();

    if (dbError) {
      console.error('❌ Erreur sauvegarde DB:', dbError);
      throw dbError;
    }
    console.log('✅ Enregistrement DB créé:', dbRecord.id);

    // 14. Télécharger automatiquement le fichier
    if (pdfBlob) {
      console.log('⬇️  Téléchargement automatique du PDF...');
      saveAs(pdfBlob, pdfFileName);
    } else {
      console.log('⬇️  Téléchargement automatique du DOCX...');
      saveAs(blob, docxFileName);
    }

    console.log('=== ✅ GÉNÉRATION TERMINÉE AVEC SUCCÈS ===');
    return dbRecord;

  } catch (error) {
    console.error('=== ❌ ERREUR GÉNÉRATION DOCUMENT ===');
    console.error('Type d\'erreur:', error.name);
    console.error('Message:', error.message);

    // Debug spécifique pour les erreurs Docxtemplater
    if (error.properties) {
      console.error('=== DÉTAILS ERREUR DOCXTEMPLATER ===');
      console.error('ID:', error.properties.id);
      console.error('Explication:', error.properties.explanation);

      if (error.properties.errors) {
        console.error('Erreurs multiples:');
        error.properties.errors.forEach((err, index) => {
          console.error(`  Erreur ${index + 1}:`, {
            message: err.message,
            part: err.part,
            offset: err.offset,
            properties: err.properties
          });
        });
      }
    }

    console.error('Stack:', error.stack);
    console.error('Données envoyées:', data);

    // Créer un message d'erreur détaillé
    let errorMessage = error.message;
    if (error.properties && error.properties.errors) {
      const firstError = error.properties.errors[0];

      if (firstError.message && firstError.message.includes('Duplicate')) {
        errorMessage = `❌ PROBLÈME DE FORMATAGE DANS LE TEMPLATE WORD

Le template Word contient des variables {{}} cassées par le formatage.

🔧 COMMENT CORRIGER :

1. Ouvrez le fichier ${type}.docx
2. Pour CHAQUE variable ({{name}}, {{date}}, etc.) :
   a. Supprimez complètement la variable
   b. Retapez-la EN UNE SEULE FOIS sans jamais :
      - Faire CTRL+Z (annuler)
      - Changer le formatage au milieu
      - Copier-coller depuis un autre endroit
   c. Appliquez le formatage APRÈS avoir tapé la variable complète

3. Sauvegardez le fichier

💡 ASTUCE : Tapez d'abord toutes vos variables en texte brut, puis appliquez le formatage ensuite.

Variables détectées avec problèmes : ${error.properties.errors.length}`;
      } else {
        const details = error.properties.errors.map(err => err.message).join(', ');
        errorMessage = `Erreur template: ${details}`;
      }
    }

    throw new Error(errorMessage);
  }
};